import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, NgxsSimpleChange, Store } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { MrfActions } from '../actions/mrf.actions';
import { Mrf } from '../models/mrf.model';
import { MrfLineItem } from '../models/mrf-line-item.model';
import { MrfPhoto } from '../models/mrf-photo.model';
import { MrfService } from 'src/app/services/mrf.service';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';
import { DateTime } from 'luxon';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged } from './state-content-changed'

export class MrfStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    mrfs: Mrf[]
}

@State<MrfStateModel>({
    name: 'mrf',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        mrfs: []
    }
})
@Injectable()
export class MrfState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private mrfService: MrfService,        
        private networkService: NetworkService,
        private offline: OfflineService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<MrfStateModel>) {   
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {
                        await this.reloadStateFromStorage()                        
                        this.store.dispatch(new MrfActions.IncrementPendingCount())
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {               
        if (this.storageService.is_initialized && stateContentChanged(change)) {
            const state_model: MrfStateModel = change.currentValue  
            this.storageService.set('mrfs', JSON.stringify(state_model))    
        }  
        if (this.networkService.currentStatus === ConnectionStatusEnum.Online) {     
            if (change.previousValue && change.previousValue.pending_count < change.currentValue.pending_count) {    
                const state_model: MrfStateModel = change.currentValue                     
                await this.offline.processPendingMrfs(state_model.mrfs)                        
            }
        }       
    }

    async reloadStateFromStorage() {
        const json: string = await this.storageService.get('mrfs');
        if (json) {
            const state_model: MrfStateModel = JSON.parse(json)
            if (state_model) {
                this.store.dispatch(new MrfActions.Init(state_model));
                return
            }
        }
        this.store.dispatch(new MrfActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        const objs = await this.mrfService.getMine()    
        if (objs != null) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new MrfActions.Merge(objs))
          return true
        }
        return false
    }

    @Selector()
    static getLastUpdate(state: MrfStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: MrfStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: MrfStateModel) {
        return state
    }

    @Selector()
    static getAll(state: MrfStateModel) {
        return state.mrfs
    }

    @Selector()
    static getOne(state: MrfStateModel) {
        return (id: number) => {
            return state.mrfs.filter(s => s.id === id)
        }
    }

    @Action(MrfActions.Refresh)
    async refresh({patchState}: StateContext<MrfStateModel>, {  }:MrfActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(MrfActions.IncrementPendingCount)
    incrementPendingCount({patchState, getState}: StateContext<MrfStateModel>, {  }:MrfActions.IncrementPendingCount) {     
        const state = getState()
        patchState({
            pending_count: state.pending_count + 1
        })
    }
    
    @Action(MrfActions.Init)
    init({setState, getState}: StateContext<MrfStateModel>, { payload }:MrfActions.Init) {             
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new MrfActions.Refresh())
    }

    @Action(MrfActions.Set)
    set({setState, getState}: StateContext<MrfStateModel>, { payload }:MrfActions.Set) {  
        const state = getState()        
        setState({   
            is_refreshing: state.is_refreshing,  
            pending_count: 0,
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),         
            mrfs: payload
        })
    }

    @Action(MrfActions.Merge)
    merge({getState, setState}: StateContext<MrfStateModel>, { payload }:MrfActions.Merge) {   
        // Server data replaces the app's, except for MRFs with a save or
        // delete still to send: those keep the local copy, whether or not the
        // server has them yet. An MRF matches by uuid, or by id once it has one.
        const state = getState()
        const local = state.mrfs
        const isPending = (m: Mrf) => m.is_pending_save || m.is_pending_delete
        const newMrfs: Mrf[] = []

        for (const server of payload) {
            const mine = local.find(m => sameMrf(m, server))
            if (mine && isPending(mine)) continue
            newMrfs.push(mine ? keepLocalOnly(server, mine) : server)
        }
        for (const mine of local) {
            if (isPending(mine)) newMrfs.push(mine)
        }

        setState({
            is_refreshing: false,
            pending_count: 0,
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),
            mrfs: newMrfs
        })
    }

    @Action(MrfActions.AddOne)
    addOne({getState, patchState}: StateContext<MrfStateModel>, { payload }:MrfActions.AddOne) {
        const state = getState()        
        patchState({
            mrfs: [...state.mrfs, payload]
        })
    }

    // By uuid: an MRF that hasn't reached the server yet has id 0, and so
    // does every other one like it.
    @Action(MrfActions.UpdateOne)
    updateOne({setState}: StateContext<MrfStateModel>, { payload }:MrfActions.UpdateOne) {
        setState(
            patch<MrfStateModel>
                ({
                    mrfs: updateItem((item: Mrf) => item.uuid === payload.uuid, 
                        patch<Mrf>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(MrfActions.DeleteOne)
    deleteOne({setState}: StateContext<MrfStateModel>, { payload }:MrfActions.DeleteOne) {
        setState(
            patch<MrfStateModel>
                ({
                    mrfs: updateItem((item: Mrf) => item.uuid === payload.uuid, 
                        patch<Mrf>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(MrfActions.ApiDeletedOne)
    apiDeletedOne({ setState }: StateContext<MrfStateModel>, { payload }:MrfActions.ApiDeletedOne) {         
        setState(
            patch<MrfStateModel>
                ({
                    mrfs: removeItem((item: Mrf) => item.uuid === payload.uuid),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(MrfActions.ApiSavedOne)
    apiSavedOne({ getState, setState }: StateContext<MrfStateModel>, { payload }:MrfActions.ApiSavedOne) {
        const item = getState().mrfs.find(m => m.uuid === payload.uuid)
        if (!item) return
        const server = Mrf.buildOne(payload.server)
        let updated: Mrf

        if (item.local_rev !== payload.local_rev) {
            // Saved again while this one was on its way: keep those edits and
            // take only the ids, so the next send is a PUT of the same lines.
            updated = {
                ...item,
                id: server.id,
                mrf_line_items: (item.mrf_line_items || []).map(l => {
                    const s = findLine(server.mrf_line_items, l)
                    return s ? { ...l, id: s.id, uuid: s.uuid || l.uuid } : l
                }),
            } as Mrf
        } else {
            // The server's copy, which is the save itself or, if parts had
            // already taken the MRF, theirs. Photos waiting to upload stay on
            // their lines; photos removed here stay removed.
            const deletes = item.pending_photo_deletes || []
            const lines = server.mrf_line_items.map(s => {
                const mine = findLine(item.mrf_line_items, s)
                const minePhotos = mine?.photos || []
                const uploaded = (s.photos || []).filter(p => !deletes.includes(p.uuid))
                const waiting = minePhotos.filter(p => !p.uploaded && !uploaded.some(u => u.uuid === p.uuid))
                return { ...s, photos: [...uploaded, ...waiting] }
            })
            updated = {
                ...server,
                uuid: item.uuid,
                is_pending_save: true,
                is_pending_delete: item.is_pending_delete,
                pending_photo_deletes: deletes,
                local_rev: item.local_rev,
                mrf_line_items: lines,
            } as Mrf
        }

        setState(patch<MrfStateModel>({
            mrfs: updateItem((m: Mrf) => m.uuid === payload.uuid, updated),
        }))
    }

    @Action(MrfActions.ApiPhotoUploaded)
    apiPhotoUploaded({ setState }: StateContext<MrfStateModel>, { payload }:MrfActions.ApiPhotoUploaded) {
        const sent = payload.photo
        setState(patch<MrfStateModel>({
            mrfs: updateItem((m: Mrf) => m.uuid === payload.mrf_uuid, (m: Mrf) => ({
                ...m,
                mrf_line_items: (m.mrf_line_items || []).map(l => ({
                    ...l,
                    photos: (l.photos || []).map(p => p.uuid === sent.uuid
                        ? MrfPhoto.buildOne({ ...p, ...sent, base64: '', uploaded: true })
                        : p),
                })),
            }) as Mrf),
        }))
    }

    @Action(MrfActions.ApiPhotoDropped)
    apiPhotoDropped({ setState }: StateContext<MrfStateModel>, { payload }:MrfActions.ApiPhotoDropped) {
        setState(patch<MrfStateModel>({
            mrfs: updateItem((m: Mrf) => m.uuid === payload.mrf_uuid, (m: Mrf) => ({
                ...m,
                mrf_line_items: (m.mrf_line_items || []).map(l => ({
                    ...l,
                    photos: (l.photos || []).filter(p => p.uuid !== payload.photo_uuid),
                })),
            }) as Mrf),
        }))
    }

    @Action(MrfActions.ApiPhotoDeleted)
    apiPhotoDeleted({ setState }: StateContext<MrfStateModel>, { payload }:MrfActions.ApiPhotoDeleted) {
        setState(patch<MrfStateModel>({
            mrfs: updateItem((m: Mrf) => m.uuid === payload.mrf_uuid, (m: Mrf) => ({
                ...m,
                pending_photo_deletes: (m.pending_photo_deletes || []).filter(u => u !== payload.photo_uuid),
            }) as Mrf),
        }))
    }

    @Action(MrfActions.ApiSyncedOne)
    apiSyncedOne({ getState, setState }: StateContext<MrfStateModel>, { payload }:MrfActions.ApiSyncedOne) {
        const item = getState().mrfs.find(m => m.uuid === payload.uuid)
        if (!item || item.local_rev !== payload.local_rev) return
        const waiting = (item.mrf_line_items || []).some(l => (l.photos || []).some(p => !p.uploaded))
        if (waiting || (item.pending_photo_deletes || []).length) return
        setState(patch<MrfStateModel>({
            mrfs: updateItem((m: Mrf) => m.uuid === payload.uuid, patch<Mrf>({ is_pending_save: false })),
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),
        }))
    }
}

function sameMrf(a: Mrf, b: Mrf): boolean {
    return (!!a.uuid && a.uuid === b.uuid) || (a.id > 0 && a.id === b.id)
}

function findLine(lines: MrfLineItem[] | undefined, target: MrfLineItem): MrfLineItem | undefined {
    if (!lines) return undefined
    return lines.find(l => !!l.uuid && l.uuid === target.uuid)
        ?? (target.id > 0 ? lines.find(l => l.id === target.id) : undefined)
}

// What only this device knows about an MRF, kept across a refresh: its uuid
// (older MRFs have none on the server, so buildOne makes one up each time).
function keepLocalOnly(server: Mrf, mine: Mrf): Mrf {
    return {
        ...server,
        uuid: mine.uuid,
    } as Mrf
}
